import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Mail, Trash2 } from 'lucide-react'
import { useContacts, useSetContactStatus, useDeleteContact } from '@/hooks/data/useContacts'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import type { ContactStatus } from '@/types/domain'

const STATUSES: ContactStatus[] = ['New', 'Read', 'Replied', 'Archived']

function statusColor(s: ContactStatus) {
  return (
    {
      New: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      Read: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      Replied: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Archived: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
    }[s] ?? 'bg-gray-100 text-gray-600'
  )
}

function ContactsPage() {
  const [statusFilter, setStatusFilter] = useState<ContactStatus | ''>('')
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const currentCursor = cursorStack[cursorStack.length - 1]
  const { data, isLoading, isError, error } = useContacts(statusFilter || undefined, currentCursor)
  const setStatus = useSetContactStatus()
  const del = useDeleteContact()
  const confirmDelete = useDeleteConfirm()
  // Holding an id (not the object) so the derived `selected` below always reflects the
  // freshest list data after a status change invalidates the query — no stale rowVersion.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = data?.items.find((c) => c.id === selectedId) ?? null

  const changeFilter = (s: ContactStatus | '') => {
    setStatusFilter(s)
    setCursorStack([])
    setSelectedId(null)
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Mail className="w-6 h-6" /> Contact Requests
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Public contact-form submissions (ContactsController).</p>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => changeFilter('')} className={`px-3 py-1.5 rounded-lg text-sm ${!statusFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => changeFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {isLoading ? (
            <p className="p-4 text-sm text-gray-500">Loading…</p>
          ) : isError ? (
            <p className="p-4 text-sm text-red-600">Failed to load: {(error as Error)?.message}.</p>
          ) : data?.items.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No submissions.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {data?.items.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`block w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedId === c.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">{c.name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${statusColor(c.status)}`}>{c.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{c.subject}</div>
                  <div className="text-[10px] text-gray-400">{new Date(c.createdAt).toLocaleString()}</div>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-sm">
            <button
              onClick={() => setCursorStack((prev) => prev.slice(0, -1))}
              disabled={cursorStack.length === 0}
              className="text-blue-600 hover:underline disabled:opacity-30 disabled:no-underline"
            >
              ← Newer
            </button>
            <button
              onClick={() => data?.nextCursor && setCursorStack((prev) => [...prev, data.nextCursor!])}
              disabled={!data?.hasMore}
              className="text-blue-600 hover:underline disabled:opacity-30 disabled:no-underline"
            >
              Older →
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          {!selected ? (
            <p className="text-sm text-gray-500">Select a submission to view details.</p>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selected.name}</h2>
                  <p className="text-sm text-gray-500">
                    {selected.email} {selected.phone && `· ${selected.phone}`}
                  </p>
                </div>
                <button
                  onClick={() =>
                    confirmDelete(selected.name, async () => {
                      await del.mutateAsync(selected.id)
                      setSelectedId(null)
                    })
                  }
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{selected.subject}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{selected.message}</p>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-gray-500">Status:</span>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    disabled={selected.status === s || setStatus.isPending}
                    onClick={() => setStatus.mutate({ id: selected.id, status: s, rowVersion: selected.rowVersion })}
                    className={`px-2 py-1 rounded-md text-xs disabled:opacity-40 ${statusColor(s)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/contacts/')({
  component: ContactsPage,
})
